import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { INTEREST_LABELS, SUCCESS_MESSAGES } from "@/lib/gateways";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistDialog = ({ open, onOpenChange, interestId, ctaLabel }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset shortly after close animation finishes
      const t = setTimeout(() => {
        setName("");
        setEmail("");
        setMessage("");
        setSuccess(false);
        setSubmitting(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const interestLabel = INTEREST_LABELS[interestId] || "General Inquiry";
  const successCopy =
    SUCCESS_MESSAGES[interestId] || SUCCESS_MESSAGES.general;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: name.trim(),
        email: email.trim(),
        interest: interestId || "general",
        message: message.trim() || null,
      });
      setSuccess(true);
      toast.success(successCopy.toast);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't record your entry. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="sm:max-w-[460px] border-[rgba(176,133,94,0.45)]"
        style={{ backgroundColor: "#F5E8E0" }}
      >
        <DialogHeader className="text-left">
          <p
            className="font-sans-body text-[11px] tracking-[0.28em] uppercase"
            style={{ color: "var(--olive)" }}
            data-testid="waitlist-interest-label"
          >
            {interestLabel}
          </p>
          <DialogTitle className="font-serif-display text-3xl sm:text-4xl font-medium leading-[1.05]">
            {success ? successCopy.title : ctaLabel || "Join the waitlist"}
          </DialogTitle>
          <DialogDescription className="font-sans-body text-[13px] leading-relaxed text-[#6f6560]">
            {success ? successCopy.description : successCopy.prompt}
          </DialogDescription>
          {!success && (
            <p
              className="font-sans-body text-[11px] italic pt-1"
              style={{ color: "var(--stone)" }}
            >
              Your details are kept private — never shared, never sold.
            </p>
          )}
        </DialogHeader>

        {!success && (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 pt-2"
            data-testid="waitlist-form"
          >
            <input type="hidden" value={interestId || "general"} readOnly />

            <div className="space-y-1.5">
              <Label
                htmlFor="wl-name"
                className="font-sans-body text-[11px] tracking-[0.22em] uppercase"
                style={{ color: "var(--stone-deep)" }}
              >
                Name
              </Label>
              <Input
                id="wl-name"
                data-testid="waitlist-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none border-0 border-b border-[rgba(176,133,94,0.6)] bg-transparent px-0 font-serif-display text-xl focus-visible:ring-0 focus-visible:border-[var(--olive)]"
                autoComplete="name"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="wl-email"
                className="font-sans-body text-[11px] tracking-[0.22em] uppercase"
                style={{ color: "var(--stone-deep)" }}
              >
                Email
              </Label>
              <Input
                id="wl-email"
                data-testid="waitlist-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-0 border-b border-[rgba(176,133,94,0.6)] bg-transparent px-0 font-serif-display text-xl focus-visible:ring-0 focus-visible:border-[var(--olive)]"
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="wl-message"
                className="font-sans-body text-[11px] tracking-[0.22em] uppercase"
                style={{ color: "var(--stone-deep)" }}
              >
                A note (optional)
              </Label>
              <Textarea
                id="wl-message"
                data-testid="waitlist-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="rounded-none border border-[rgba(176,133,94,0.5)] bg-transparent font-sans-body text-sm focus-visible:ring-0 focus-visible:border-[var(--olive)]"
              />
            </div>

            <DialogFooter className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="waitlist-submit-button"
                className="btn-olive w-full px-6 py-3 text-[11px] uppercase font-sans-body disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Send"}
              </button>
            </DialogFooter>
          </form>
        )}

        {success && (
          <div className="pt-2" data-testid="waitlist-success">
            <div className="rule-ornament py-4">
              <span className="font-serif-display italic text-sm">selah</span>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="waitlist-close-button"
              className="btn-ghost-olive w-full px-6 py-3 text-[11px] uppercase font-sans-body"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
